/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import { toast } from 'react-toastify';
import { cnpjMask, cpfMask, phoneMask } from '../../../utils/masks';
import userHasRoles from '../../../utils/userHasRoles';
import Role from '../../../constants/roles';
import { AuthValues, useAuth } from '../../../contexts/authContext';
import sellerStatus from '../../../constants/sellerStatus';
import UserHttpService from '../../../services/http/user-http';
import { normalizeCityName } from '../../../utils/strings';
import { SINTEGRA_HOMO } from '../../../config/portal';
import customerInputsLength from '../../../constants/customerInputsLength';
import { StyledFormControl, StyledSelect } from './style';
import BrasilService from '../../../services/http/brasil.http';
import IntegratorHttpService from '../../../services/http/integrator-http';
import SelectAsyncPaginate from '../../AsyncSelect';

const CustomerUser = (props: any) => {
    const { user }: AuthValues = useAuth();
    const isIntegrator = userHasRoles(user, [
        Role.Integrator,
        Role.Manager,
        Role.Contributor,
    ]);
    const [loadingSintegraData, setLoadingSintegraData] = useState(false);

    async function loadIntegrators(page: number, search?: string) {
        const data: any = {
            statusFilter: sellerStatus.Approved,
            isShortList: true,
            page,
            take: 10,
        };
        if (search && Number.isNaN(parseInt(search, 10))) {
            data.term = search;
        }
        if (search && !Number.isNaN(parseInt(search, 10))) {
            data.erpCode = search;
        }
        const response: any = await IntegratorHttpService.readMany(data);

        return response.data.data as any[];
    }

    const getCitiesByState = async (state: string) => {
        const response = await BrasilService.getCitiesByState(state);
        if (!response.length) {
            return;
        }

        // eslint-disable-next-line arrow-body-style
        const cities = response.map((obj) => {
            return {
                label: obj.name,
                value: obj.id,
            };
        });

        // eslint-disable-next-line consistent-return
        return cities;
    };

    // eslint-disable-next-line consistent-return
    const handleCnpjChanges = async (event: any) => {
        const value: any = event?.target?.value?.replace(/\D/g, '');

        props.handleChanges([{ target: { name: 'document', value } }]);

        const changes: any = [];

        if (value.length === 14 && !props.errors.document && !SINTEGRA_HOMO) {
            setLoadingSintegraData(true);

            try {
                const { data } = await UserHttpService.cnpjConsultation(value);

                if (!data) {
                    return toast.info(
                        'Não foi possível consultar o CNPJ. Prossiga com o cadastro manualmente.',
                    );
                }

                const cities = await getCitiesByState(data?.state);
                const city: any = cities?.find(
                    (item: any) =>
                        normalizeCityName(item?.label) ===
                        normalizeCityName(data?.city),
                );

                changes.push({ target: { name: 'document', value } });
                changes.push({
                    target: {
                        name: 'fancyName',
                        value: data?.fancyName?.substring(
                            0,
                            customerInputsLength.fancyName,
                        ),
                    },
                });
                changes.push({
                    target: {
                        name: 'name',
                        value: data?.name?.substring(
                            0,
                            customerInputsLength.name,
                        ),
                    },
                });

                if (
                    data?.stateRegistration &&
                    data.stateRegistration !== 'ISENTO'
                ) {
                    changes.push({ target: { name: 'hasIe', value: true } });
                    changes.push({
                        target: {
                            name: 'ie',
                            value: data?.stateRegistration,
                        },
                    });
                } else {
                    changes.push({ target: { name: 'hasIe', value: false } });
                    changes.push({ target: { name: 'ie', value: 'ISENTO' } });
                }

                changes.push({
                    target: {
                        name: 'erpState',
                        value: { label: data?.state, value: data?.state },
                    },
                });
                changes.push({
                    target: {
                        name: 'erpCity',
                        value: city.value || data?.erpCity,
                    },
                });
                changes.push({
                    target: {
                        name: 'erpCityName',
                        value: city.label || data?.city,
                    },
                });
                changes.push({
                    target: {
                        name: 'addressCep',
                        value: data?.cep.toString(),
                    },
                });
                changes.push({
                    target: {
                        name: 'addressDescription',
                        value: data?.addressDescription?.substring(
                            0,
                            customerInputsLength.addressDescription,
                        ),
                    },
                });
                changes.push({
                    target: {
                        name: 'addressNeighborhood',
                        value: data?.addressNeighborhood?.substring(
                            0,
                            customerInputsLength.addressNeighborhood,
                        ),
                    },
                });
                changes.push({
                    target: {
                        name: 'addressNumber',
                        value: data?.addressNumber?.substring(
                            0,
                            customerInputsLength.addressNumber,
                        ),
                    },
                });
                changes.push({
                    target: {
                        name: 'addressComplement',
                        value: data?.addressComplement?.substring(
                            0,
                            customerInputsLength.addressComplement,
                        ),
                    },
                });

                props.handleChanges(changes);
            } catch (err) {
                const error = err as any;
                toast.error(
                    error.message ||
                        'Não foi possível consultar o CNPJ. Prossiga com o cadastro manualmente',
                );
            } finally {
                setLoadingSintegraData(false);
            }

            setLoadingSintegraData(false);
        }
    };

    // eslint-disable-next-line consistent-return
    const getSellerData = async () => {
        if (props.customer.sellerCode && props.customer.id) {
            const sellerCodeFilter: any =
                typeof props.customer.sellerCode === 'object'
                    ? props.customer.sellerCode.value
                    : props.customer.sellerCode;

            const integrators = await loadIntegrators(1, `${sellerCodeFilter}`);
            const integrator = integrators?.find(
                (item: any) =>
                    parseInt(`${item.erpCode}`, 10) === sellerCodeFilter,
            );

            if (integrator) {
                props.handleChanges([
                    {
                        target: {
                            name: 'sellerCode',
                            value: {
                                label: integrator.name,
                                value: integrator.erpCode,
                            },
                        },
                    },
                ]);
                props.setLoadIntegrator(integrator);
                props.setLoadIntegratorOptions(integrators);
            }
        }
    };

    const findIntegrators = async (page: number, search?: string) => {
        if (props.customer.sellerCode && props.customer.id)
            return props.loadIntegrator;
        const integrators = await loadIntegrators(page, search);
        return integrators;
    };

    useEffect(() => {
        getSellerData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.customer.id]);

    return (
        <>
            <Row>
                {!isIntegrator && (
                    <Col lg={12} md={12}>
                        <Form.Group controlId="formBasicSeller">
                            <Form.Label>Vendedor responsável</Form.Label>
                            <Form.Control
                                style={{ display: 'none' }}
                                isInvalid={!!props.errors.sellerCode}
                            />
                            <StyledSelect>
                                <SelectAsyncPaginate
                                    keyValue="erpCode"
                                    isDisabled={
                                        props.customer.sellerCode &&
                                        props.customer.id
                                    }
                                    keyLabel="name"
                                    searchData={props.customer.sellerCode}
                                    className="absolute border-maroon z4"
                                    value={props.loadIntegrator}
                                    defaultOptions={props.loadIntegratorOptions}
                                    isSearchable
                                    label="Selecione..."
                                    loadOptionsData={findIntegrators}
                                    setData={(integrator) => {
                                        props.setLoadIntegrator(integrator);
                                        props.handleChanges([
                                            {
                                                target: {
                                                    name: 'sellerCode',
                                                    value: integrator.erpCode,
                                                },
                                            },
                                        ]);
                                    }}
                                />
                            </StyledSelect>

                            {props.errors.sellerCode && (
                                <Form.Control.Feedback type="invalid">
                                    {props.errors.sellerCode}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                )}
                {props.documentType === 'J' ? (
                    <>
                        <Col lg={12} md={12}>
                            {loadingSintegraData && !props.errors.cnpj && (
                                <>
                                    <p>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="mr-2"
                                        />
                                        Buscando informações da empresa...
                                    </p>
                                </>
                            )}
                        </Col>
                        <Col lg={12} md={12}>
                            <Form.Group controlId="formBasicName">
                                <Form.Label>Nome Fantasia</Form.Label>
                                <Form.Control
                                    disabled={props.customer.id}
                                    value={props.customer.fancyName}
                                    onChange={props.handleChanges}
                                    name="fancyName"
                                    maxLength={customerInputsLength.fancyName}
                                    type="text"
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={6} md={12}>
                            <Form.Group controlId="formBasicCnpj">
                                <Form.Label>CNPJ</Form.Label>
                                {loadingSintegraData}
                                <Form.Control
                                    disabled={props.customer.id}
                                    value={props.customer.document}
                                    isInvalid={!!props.errors.document}
                                    as={MaskedInput}
                                    mask={cnpjMask}
                                    onChange={handleCnpjChanges}
                                    name="document"
                                    type="text"
                                />
                                {props.errors.document && (
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.document}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>
                    </>
                ) : (
                    <Col lg={6} md={12}>
                        <Form.Group controlId="formBasicName">
                            <Form.Label>CPF</Form.Label>
                            <Form.Control
                                disabled={props.customer.id}
                                value={props.customer.document}
                                isInvalid={!!props.errors.document}
                                as={MaskedInput}
                                mask={cpfMask}
                                onChange={props.handleChanges}
                                name="document"
                                type="text"
                            />
                            <Form.Control.Feedback type="invalid">
                                {props.errors.document}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                )}
                <Col lg={6} md={12}>
                    <Form.Group controlId="formBasicIe">
                        <Form.Label>Possui Inscrição Estadual?</Form.Label>
                        <Form.Check
                            id="switchIe"
                            name="hasIe"
                            type="switch"
                            checked={props.customer.hasIe}
                            className="float-left"
                            onChange={props.handleChanges}
                        />
                        <StyledFormControl
                            disabled={
                                props.isDisabled ||
                                props.customer.hasIe !== true
                            }
                            value={
                                props.customer.ie === 'ISENTO'
                                    ? ''
                                    : props.customer.ie
                            }
                            isInvalid={!!props.errors.ie}
                            onChange={props.handleChanges}
                            name="ie"
                            type="text"
                            placeholder={
                                props.customer.hasIe !== false
                                    ? 'Digite a Inscrição Estadual...'
                                    : 'Isento'
                            }
                        />
                        {props.errors.ie && (
                            <Form.Control.Feedback type="invalid">
                                {props.errors.ie}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
                <Col lg={12} md={12}>
                    <Form.Group controlId="formBasicName">
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                            disabled={props.customer.id}
                            isInvalid={!!props.errors.name}
                            onChange={props.handleChanges}
                            value={props.customer.name}
                            name="name"
                            type="text"
                            maxLength={customerInputsLength.name}
                        />

                        <Form.Control.Feedback type="invalid">
                            {props.errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col lg={6} md={12}>
                    <Form.Group controlId="formBasicPhone">
                        <Form.Label>Telefone</Form.Label>
                        <Form.Control
                            value={props.customer.phone}
                            isInvalid={!!props.errors.phone}
                            as={MaskedInput}
                            mask={phoneMask}
                            onChange={props.handleChanges}
                            name="phone"
                            type="tel"
                        />
                        <Form.Control.Feedback type="invalid">
                            {props.errors.phone}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col lg={6} md={12}>
                    <Form.Group controlId="formBasicEmail">
                        <Form.Label>
                            E-mail (utilize exclusivamente o e-mail do cliente)
                        </Form.Label>
                        <Form.Control
                            isInvalid={!!props.errors.email}
                            onChange={props.handleChanges}
                            value={props.customer.email}
                            name="email"
                            maxLength={100}
                            type="email"
                        />

                        <Form.Control.Feedback type="invalid">
                            {props.errors.email}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
};

export default CustomerUser;
